import { configureStore as createReduxStore } from "@reduxjs/toolkit";
import { persistStore } from "redux-persist";
import promiseMiddleware from "./utils/promiseMiddleware";
import plainRootReducer from "./reducers";
import {
	buildPersistedRootReducer,
	loadPersistedCurrency,
	migrateLegacyPersistedState,
	persistCurrencyOnChange,
} from "app/utils/persistor";

let store;
let persistor;

/**
 * @param {object} initialState preloaded state (SSR payload on the client)
 * @param {object} [options]
 * @param {boolean} [options.persist=false] enable redux-persist (client runtime only).
 *        Left off for SSR and unit tests so the state shape stays free of `_persist` keys.
 */
export function configureStore(initialState, { persist = false } = {}) {
	if (persist) {
		// Convert any legacy redux-persist v4 storage to the v6 layout before rehydration,
		// so upgrading users keep their persisted state instead of starting from scratch.
		migrateLegacyPersistedState(initialState?.shop);
	}
	const rootReducer = persist ? buildPersistedRootReducer(initialState?.shop) : plainRootReducer;

	// currency is persisted manually (primitive slice); a stored value wins over the SSR default.
	let preloadedState = initialState;
	if (persist) {
		const persistedCurrency = loadPersistedCurrency();
		if (persistedCurrency !== undefined) {
			preloadedState = { ...initialState, currency: persistedCurrency };
		}
	}

	store = createReduxStore({
		reducer: rootReducer,
		preloadedState,
		devTools: process.env.NODE_ENV !== "production" && { trace: true },
		// Serializable/immutable checks are dev-only and incompatible with our patterns:
		// promiseMiddleware passes a Promise on the action, and redux-persist dispatches
		// non-serializable register payloads. Disabling them preserves the legacy behavior.
		middleware: getDefaultMiddleware =>
			getDefaultMiddleware({
				serializableCheck: false,
				immutableCheck: false,
			}).concat(promiseMiddleware),
	});

	if (persist) {
		persistor = persistStore(store);
		persistCurrencyOnChange(store);
	}

	if (typeof window !== "undefined" && window.Cypress) {
		window.store = store;
	}

	if (process.env.NODE_ENV !== "production" && module.hot) {
		// Enable Webpack hot module replacement for reducers
		module.hot.accept("./reducers", () => {
			const nextRootReducer = require("./reducers");
			store.replaceReducer(
				persist ? buildPersistedRootReducer(initialState?.shop) : nextRootReducer.default
			);
		});
	}

	return store;
}

export function getStore() {
	return store;
}

export function getPersistor() {
	return persistor;
}
